/*
 * REDFORD FX library · Ink Corner Reveal
 * Własna implementacja (clean-room) zasady przejścia zaobserwowanej na immersive-g.com (Awwwards SOTD 01.2025).
 * Nie zawiera kodu, tekstur ani shaderów z oryginału. Szum proceduralny, bez zewnętrznych zależności (czysty WebGL1).
 *
 * Zasada: pełnoekranowy quad; maska rośnie od narożnika (domyślnie prawy górny) jako funkcja odległości od narożnika,
 * a jej krawędź jest „poszarpana” dwiema warstwami animowanego szumu fbm, wydłużonego wzdłuż przekątnej.
 * Stany: rest (0) → peek (hover, ok. 0.23 ekranu) → full (klik, cały ekran). Postęp animowany z easingiem.
 *
 * API:
 *   const fx = InkCornerReveal.create(canvas, opts)
 *   fx.to('rest'|'peek'|'full'|number, durationSec?)   // przejście do stanu
 *   fx.set(number)                                      // natychmiastowe ustawienie postępu
 *   fx.destroy()
 * opts: inkColor [r,g,b 0..1], paperColor [r,g,b] | null (przezroczyste tło), corner 'tr'|'tl'|'br'|'bl',
 *       peek (0..1), edge (miękkość krawędzi), noiseScale, wobble, maxDpr, reducedMotion (auto)
 */
(function (global) {
  'use strict';

  var VERT = 'attribute vec2 aPos;void main(){gl_Position=vec4(aPos,0.0,1.0);}';

  var FRAG = [
    'precision highp float;',
    'uniform vec2 uRes;uniform float uTime;uniform float uProgress;uniform vec3 uInk;uniform vec3 uPaper;',
    'uniform float uPaperAlpha;uniform vec2 uCorner;uniform float uEdge;uniform float uNoiseScale;uniform float uWobble;',
    // hash + value noise + fbm (własne)
    'float h(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}',
    'float vn(vec2 p){vec2 i=floor(p);vec2 f=fract(p);vec2 u=f*f*(3.0-2.0*f);',
    ' return mix(mix(h(i),h(i+vec2(1.0,0.0)),u.x),mix(h(i+vec2(0.0,1.0)),h(i+vec2(1.0,1.0)),u.x),u.y);}',
    'float fbm(vec2 p){float v=0.0;float a=0.5;for(int i=0;i<5;i++){v+=a*vn(p);p=p*2.03+vec2(17.1,9.2);a*=0.5;}return v;}',
    'mat2 rot(float a){float c=cos(a),s=sin(a);return mat2(c,-s,s,c);}',
    'void main(){',
    ' vec2 uv=gl_FragCoord.xy/uRes;float aspect=uRes.x/uRes.y;',
    // odległość od narożnika w przestrzeni z zachowaniem proporcji; 1.0 ≈ długość przekątnej ekranu
    ' vec2 d=(uv-uCorner)*vec2(aspect,1.0);float diag=length(vec2(aspect,1.0));float dist=length(d)/diag;',
    // szum wydłużony wzdłuż przekątnej narożnika → „strugi” atramentu
    ' float ang=atan(1.0,aspect);vec2 q=rot(ang)*(d*uNoiseScale);q.y*=0.45;',
    ' float t=uTime;',
    ' float n1=fbm(q*3.0+vec2(0.0,t*0.06));',
    ' float n2=fbm(q*9.0-vec2(t*0.11,0.0)+n1*1.7);',
    ' float wob=sin(q.y*38.0+t*1.3)*0.012*uWobble+cos(q.y*21.0-t*0.9)*0.01*uWobble;',
    ' float n=mix(n1,n2,0.35);',
    // czoło atramentu: zasięg = postęp; szum przesuwa czoło lokalnie (±) → nieregularna, żywa krawędź
    ' float front=uProgress*1.15;',
    ' float field=front-dist+(n-0.5)*0.34*smoothstep(0.0,0.08,uProgress)+wob;',
    ' float ink=smoothstep(0.0,uEdge,field);',
    // pełne pokrycie na końcu (bez „dziur” szumu)
    ' ink=mix(ink,1.0,smoothstep(0.93,1.0,uProgress));',
    // delikatne przyciemnienie tuż przy krawędzi (efekt mokrego brzegu)
    ' float rim=smoothstep(uEdge*3.0,0.0,abs(field))*0.18*(1.0-ink)*step(0.001,uProgress);',
    ' vec3 paper=uPaper;vec3 col=mix(paper,uInk,ink);col=mix(col,uInk,rim);',
    ' float a=max(ink+rim,uPaperAlpha);',
    ' gl_FragColor=vec4(col*a,a);', // premultiplied
    '}'
  ].join('\n');

  var CORNERS = { tr: [1, 1], tl: [0, 1], br: [1, 0], bl: [0, 0] };
  var STATES = { rest: 0.0, peek: 0.23, full: 1.0 };

  function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; } // cubic in-out

  function compile(gl, type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { throw new Error(gl.getShaderInfoLog(s)); }
    return s;
  }

  function create(canvas, opts) {
    opts = opts || {};
    var reduced = opts.reducedMotion != null ? opts.reducedMotion
      : (global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var gl = canvas.getContext('webgl', { premultipliedAlpha: true, alpha: true, antialias: false });
    var paper = opts.paperColor === null ? null : (opts.paperColor || [0.914, 0.906, 0.886]);
    var ink = opts.inkColor || [0.05, 0.05, 0.06];
    var fallback = !gl;

    // Fallback bez WebGL: ten sam przebieg stanów, zwykły CSS clip-path (koło z narożnika)
    if (fallback) {
      var el = document.createElement('div');
      var c = CORNERS[opts.corner || 'tr'];
      el.style.cssText = 'position:fixed;inset:0;pointer-events:none;transition:clip-path .9s cubic-bezier(.65,0,.35,1);' +
        'background:rgb(' + ink.map(function (v) { return Math.round(v * 255); }).join(',') + ');';
      var at = (c[0] ? '100%' : '0%') + ' ' + (c[1] ? '0%' : '100%');
      var apply = function (p) { el.style.clipPath = 'circle(' + (p * 150) + '% at ' + at + ')'; };
      apply(0); canvas.parentNode.insertBefore(el, canvas.nextSibling); canvas.style.display = 'none';
      return { to: function (s) { apply(typeof s === 'number' ? s : STATES[s]); }, set: function (p) { apply(p); },
               destroy: function () { el.remove(); }, webgl: false };
    }

    var prog = gl.createProgram();
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
    gl.useProgram(prog);
    var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW); // jeden trójkąt na cały ekran
    var loc = gl.getAttribLocation(prog, 'aPos'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    var U = {}; ['uRes', 'uTime', 'uProgress', 'uInk', 'uPaper', 'uPaperAlpha', 'uCorner', 'uEdge', 'uNoiseScale', 'uWobble']
      .forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
    gl.uniform3fv(U.uInk, ink); gl.uniform3fv(U.uPaper, paper || [0, 0, 0]); gl.uniform1f(U.uPaperAlpha, paper ? 1 : 0);
    gl.uniform2fv(U.uCorner, CORNERS[opts.corner || 'tr']);
    gl.uniform1f(U.uEdge, opts.edge || 0.035); gl.uniform1f(U.uNoiseScale, opts.noiseScale || 1.0);
    gl.uniform1f(U.uWobble, reduced ? 0.0 : (opts.wobble != null ? opts.wobble : 1.0));
    if (opts.peek != null) STATES.peek = opts.peek;

    var maxDpr = opts.maxDpr || 1.5, progress = 0, from = 0, target = 0, t0 = 0, dur = 0, raf = 0, start = performance.now(), alive = true;
    function resize() {
      var dpr = Math.min(global.devicePixelRatio || 1, maxDpr);
      var w = Math.round(canvas.clientWidth * dpr), h = Math.round(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      gl.viewport(0, 0, w, h); gl.uniform2f(U.uRes, w, h);
    }
    function frame(now) {
      if (!alive) return;
      if (dur > 0) { var k = Math.min(1, (now - t0) / (dur * 1000)); progress = from + (target - from) * ease(k); if (k >= 1) dur = 0; }
      resize();
      gl.uniform1f(U.uTime, reduced ? 0 : (now - start) / 1000);
      gl.uniform1f(U.uProgress, progress);
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3);
      // renderuj stale tylko gdy trwa animacja albo krawędź jest widoczna (żywa krawędź); inaczej wstrzymaj pętlę
      var edgeVisible = progress > 0.001 && progress < 0.999;
      if (dur > 0 || (edgeVisible && !reduced)) raf = requestAnimationFrame(frame); else raf = 0;
    }
    function kick() { if (!raf) raf = requestAnimationFrame(frame); }
    function to(state, seconds) {
      var v = typeof state === 'number' ? state : STATES[state];
      from = progress; target = v; t0 = performance.now();
      var span = Math.abs(target - from);
      dur = reduced ? 0.001 : (seconds != null ? seconds : (target >= 0.99 ? 1.1 : 0.55) * Math.max(0.35, span / (target >= 0.99 ? 1 : 0.25)));
      if (target < from && target === 0) dur = reduced ? 0.001 : 0.6;
      kick();
    }
    function set(p) { progress = p; dur = 0; kick(); }
    global.addEventListener('resize', kick);
    kick();
    return { to: to, set: set, webgl: true, get progress() { return progress; },
             destroy: function () { alive = false; cancelAnimationFrame(raf); global.removeEventListener('resize', kick); } };
  }

  global.InkCornerReveal = { create: create, STATES: STATES };
})(typeof window !== 'undefined' ? window : this);
